import { getAccessProfileQueryOptions } from "@packages/api-client/hooks/authorizationController";
import { getMyOrganizationsQueryOptions } from "@packages/api-client/hooks/organizationController";
import {
	PlatformSubscriptionStatus,
	type UserAccessProfile,
} from "@packages/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useLocation,
	useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { ErrorPage } from "@/components/ErrorPage";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { EstablishmentProvider } from "@/hooks/useEstablishment";
import { ensureQueryDataSurvivingCancel } from "@/lib/ensureQueryDataSurvivingCancel";
import { isFullPageRoute } from "@/lib/layout/fullPageRoutes";
import { OrganizationInactive } from "./$orgId/-components/OrganizationInactive";
import { OrgBaseLayout } from "./$orgId/-components/OrgBaseLayout";
import { OrgEnteredBeacon } from "./$orgId/-components/OrgEnteredBeacon";

// Routes that should bypass subscription check (used during checkout flow)
const SUBSCRIPTION_BYPASS_ROUTES = [
	"/settings/platform-plan/success",
	"/settings/platform-plan/update",
];

/** HTTP status off an axios-shaped rejection, or undefined for non-HTTP errors. */
function errorStatus(error: unknown): number | undefined {
	const status = (error as { response?: { status?: unknown } } | null)?.response
		?.status;
	return typeof status === "number" ? status : undefined;
}

/** 4xx — client errors won't fix themselves on retry (avoids the paused-retry hang). */
function isClientError(error: unknown): boolean {
	const status = errorStatus(error);
	return status !== undefined && status >= 400 && status < 500;
}

/** 403 (not a member) or 404 (org gone) — the org isn't accessible to this user. */
function isOrgAccessDenied(error: unknown): boolean {
	const status = errorStatus(error);
	return status === 403 || status === 404;
}

export const Route = createFileRoute("/_auth/app/_app/org/$orgId")({
	// `?farm=<establishmentId>` is the source of truth for the selected farm
	// scope (absent = All Farms). Defined here so it is inherited by every
	// child route; see useEstablishment for how it is read/written.
	validateSearch: (search: Record<string, unknown>): { farm?: string } => ({
		farm:
			typeof search.farm === "string" && search.farm.length > 0
				? search.farm
				: undefined,
	}),
	loader: async ({ context, params }) => {
		const orgId = params.orgId;

		// Resolve both org-scoped queries before rendering so `useSuspenseQuery`
		// downstream finds them in cache. Without the await the loader returns
		// immediately, the component mounts, and the suspense fallback IS this
		// route's `pendingComponent` — which renders without the shell. If the
		// in-flight fetch then stalls (e.g. an apiClient-configure race), the
		// page is stuck on a content-area skeleton with no sidebar. Awaiting
		// keeps the loader pending instead, so the pending UX is intentional.
		//
		// staleTime:Infinity here means the loader only blocks on a network fetch
		// when there is NO cached data at all. When stale data exists (e.g. after
		// an offline reload from IDB), the loader returns the stale value immediately
		// and the components' own useSuspenseQuery calls handle background refresh.
		// Without this, ensureQueryData internally calls prefetchQuery which awaits
		// fetchQuery, and a paused-retry query (offline, networkMode:offlineFirst)
		// never settles — hanging the loader forever (spec AC-F7).
		// The access profile is the org gate. A membership failure (403) must
		// REJECT promptly so we can act on it — under the global retry:2 +
		// networkMode:"offlineFirst", a retried 4xx can get stuck in a paused
		// (offline) retry that never settles, hanging the loader on the pending
		// spinner forever. `retry` skips retries for client errors so the failure
		// surfaces immediately; transient 5xx/network still retry as usual.
		try {
			await ensureQueryDataSurvivingCancel(context.queryClient, {
				...getAccessProfileQueryOptions({ organizationId: orgId }),
				staleTime: Number.POSITIVE_INFINITY,
				retry: (failureCount, error) =>
					!isClientError(error) && failureCount < 2,
			});
		} catch (error) {
			// Not a member of this org (403) or the org doesn't exist (404):
			// bounce to org selection instead of an error page or infinite spinner.
			// This is the stale-bookmark case (e.g. an org link after a DB reset).
			if (isOrgAccessDenied(error)) {
				throw redirect({ to: "/app/select-organization" });
			}
			throw error;
		}

		await ensureQueryDataSurvivingCancel(context.queryClient, {
			...getMyOrganizationsQueryOptions(),
			staleTime: Number.POSITIVE_INFINITY,
		});

		return { orgId };
	},
	pendingComponent: OrganizationPendingComponent,
	component: OrganizationLayout,
	errorComponent: OrganizationErrorPage,
});

function OrganizationPendingComponent() {
	// Centered spinner instead of a bare DashboardSkeleton — when the org route
	// suspends, the OrgBaseLayout shell isn't rendered, so a skeleton reads as
	// "broken content" rather than "still loading".
	return (
		<div className="min-h-screen bg-base-100 flex items-center justify-center">
			<LoadingSpinner size="lg" />
		</div>
	);
}

function OrganizationLayout() {
	const { orgId } = Route.useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { data: accessProfile } = useSuspenseQuery(
		getAccessProfileQueryOptions({ organizationId: orgId }),
	);

	// If we cannot derive org context, bounce back to org selection
	useEffect(() => {
		if (!orgId) {
			navigate({ to: "/app/select-organization" });
		}
	}, [orgId, navigate]);

	const organizationName = accessProfile.orgName ?? orgId;
	const subscriptionStatus =
		accessProfile.subscriptionStatus as PlatformSubscriptionStatus | null;

	const isActiveSubscription =
		subscriptionStatus === PlatformSubscriptionStatus.ACTIVE ||
		subscriptionStatus === PlatformSubscriptionStatus.TRIALING;

	// Check if current route should bypass subscription check
	const shouldBypassSubscriptionCheck = SUBSCRIPTION_BYPASS_ROUTES.some(
		(route) => location.pathname.endsWith(route),
	);

	// Show inactive page only if subscription is not active AND not on bypass route
	if (!isActiveSubscription && !shouldBypassSubscriptionCheck) {
		return (
			<OrganizationInactive
				organizationName={organizationName}
				orgId={orgId}
				subscriptionStatus={subscriptionStatus}
			/>
		);
	}

	// For bypass routes, render without OrgBaseLayout (they have their own layout)
	if (shouldBypassSubscriptionCheck) {
		return <Outlet />;
	}

	// A full-page route keeps the org gate and the farm-scope provider and skips
	// only the shell — see `lib/layout/fullPageRoutes`.
	if (isFullPageRoute(location.pathname)) {
		return (
			<EstablishmentProvider orgId={orgId}>
				<OrgEnteredBeacon orgId={orgId} />
				<Outlet />
			</EstablishmentProvider>
		);
	}

	return (
		<EstablishmentProvider orgId={orgId}>
			{/* Funnel stage 3: the usable shell is exposed. Renders nothing. */}
			<OrgEnteredBeacon orgId={orgId} />
			<OrgBaseLayout
				organizationName={organizationName}
				profile={accessProfile as UserAccessProfile}
			>
				<Outlet />
			</OrgBaseLayout>
		</EstablishmentProvider>
	);
}

function OrganizationErrorPage({ error }: { error: Error }) {
	const navigate = useNavigate();

	const handleHome = () => {
		navigate({ to: "/app/select-organization" });
	};

	const handleRetry = () => {
		window.location.reload();
	};

	return (
		<ErrorPage
			error={error}
			showBackButton={true}
			showHomeButton={true}
			showRetryButton={true}
			onHome={handleHome}
			onRetry={handleRetry}
		/>
	);
}
